import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Block } from '../classes/Block';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import MergeIcon from '@mui/icons-material/Merge';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BlockCreationPopup, { type BlockCreationRequest } from './BlockCreationPopup';
import './BlockTabs.css';
import { t } from '@lingui/core/macro';
import { CardGroup, CardId, buildBlockCardMap } from '../utils/cardGroups';
import { useProject } from '../contexts/ProjectContext';
import { trackEvent } from '../statsig';
import {
  type BlockMoveRange,
  SbsBlockMeta,
  SbsDeleteDialog,
  buildSbsPairLookup,
  cloneBlocksForSource,
  getMoveRangeForBlockIndex,
  getSbsDropValidation,
  getUniqueBlockName,
  hasAnySbsBlocks,
  moveBlockRange,
  remapIndexAfterRangeMove,
} from './SBSBlockTabs';

interface BlockTabsProps {
  blocks: Block[];
  selectedBlockIndex: number | null;
  onBlockSelect: (index: number | null) => void;
  onBlocksChange: (newBlocks: Block[]) => void | Promise<void>;
  maxTabsHeight?: number;
  tabsTopOffset?: number;
  isCardsMode?: boolean;
  cardGroups?: CardGroup[];
  onSplitCardAt?: (blockId: string) => void;
  onMergeCardWithPrevious?: (cardId: CardId) => void;
  readOnly?: boolean;
}

interface VisibleBlock {
  block: Block;
  blockIndex: number;
  blockNumber: number;
}

interface PendingDeleteState {
  blockIndex: number;
  sbsMeta: SbsBlockMeta;
}

interface SortableBlockTabProps {
  visibleBlock: VisibleBlock;
  isSelected: boolean;
  isEditing: boolean;
  isHovered: boolean;
  isCardsMode: boolean;
  cardGroup?: CardGroup;
  sbsMeta?: SbsBlockMeta;
  isInvalidDropTarget: boolean;
  editingName: string;
  canSplit: boolean;
  canMerge: boolean;
  readOnly: boolean;
  disableReorder: boolean;
  onSelect: (blockIndex: number) => void;
  onMouseEnter: (blockIndex: number) => void;
  onMouseLeave: () => void;
  onRenameSubmit: (e: React.FormEvent) => void;
  onRenameCancel: () => void;
  onRenameKeyDown: (e: React.KeyboardEvent) => void;
  onEditNameChange: (value: string) => void;
  onEditClick: (e: React.MouseEvent, blockIndex: number) => void;
  onDeleteClick: (e: React.MouseEvent, blockIndex: number) => void;
  onVisibilityToggle: (e: React.MouseEvent, blockIndex: number) => void;
  onLockToggle: (e: React.MouseEvent, blockIndex: number) => void;
  onSplitCardAt?: (blockId: string) => void;
  onMergeCardWithPrevious?: (cardId: CardId) => void;
}

function createEmptyBlock(name: string, sbsBlocks: Block['sbsBlocks'] = 'normal'): Block {
  return Block.create({
    name,
    sbsBlocks,
    sbsDistance: sbsBlocks === 'left' ? Block.defaults.sbsDistance : 0,
    blockNameColor: '#ffffff',
    labelFontColor: '#ffffff',
    valueFontColor: '#ffffff',
    dataRows: [{ col0: '', col1: 'New', col2: 'Block' }],
  });
}

export function getBlockTabsMoveRangeForBlockIndex(
  blocks: Block[],
  blockIndex: number,
  isCardsMode: boolean,
  cardGroup?: CardGroup
): BlockMoveRange {
  if (isCardsMode && cardGroup && cardGroup.blockIds.length > 1) {
    const block: Block | undefined = blocks[blockIndex];
    const isFirstBlockInCard: boolean = block?.id === cardGroup.blockIds[0];

    if (isFirstBlockInCard) {
      const cardBlockIndices: number[] = cardGroup.blockIds.map((blockId: string) =>
        blocks.findIndex((candidate: Block) => candidate.id === blockId)
      );
      const hasMissingBlock: boolean = cardBlockIndices.some((index: number) => index < 0);
      const sortedCardBlockIndices: number[] = [...cardBlockIndices].sort((left: number, right: number) => left - right);
      const isContiguousCard: boolean = sortedCardBlockIndices.every(
        (index: number, sortedIndex: number) =>
          sortedIndex === 0 || index === sortedCardBlockIndices[sortedIndex - 1] + 1
      );

      if (!hasMissingBlock && isContiguousCard) {
        return {
          startIndex: sortedCardBlockIndices[0],
          endIndex: sortedCardBlockIndices[sortedCardBlockIndices.length - 1],
        };
      }
    }
  }

  return getMoveRangeForBlockIndex(blocks, blockIndex);
}

const SortableBlockTab: React.FC<SortableBlockTabProps> = ({
  visibleBlock,
  isSelected,
  isEditing,
  isHovered,
  isCardsMode,
  cardGroup,
  sbsMeta,
  isInvalidDropTarget,
  editingName,
  canSplit,
  canMerge,
  readOnly,
  disableReorder,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  onRenameSubmit,
  onRenameCancel,
  onRenameKeyDown,
  onEditNameChange,
  onEditClick,
  onDeleteClick,
  onVisibilityToggle,
  onLockToggle,
  onSplitCardAt,
  onMergeCardWithPrevious,
}) => {
  const { block, blockIndex } = visibleBlock;
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: block.id,
    disabled: isEditing || readOnly || disableReorder,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(sbsMeta
      ? ({ '--sbs-pair-color': sbsMeta.pair.color } as React.CSSProperties)
      : {}),
  };

  const isFirstInCard: boolean = isCardsMode && cardGroup !== undefined && cardGroup.blockIds[0] === block.id;
  const visibilityLabel: string = block.isHidden ? t`Show block` : t`Hide block`;
  const lockLabel: string = block.isLocked ? t`Unlock block` : t`Lock block`;
  const isActionsMenuOpen: boolean = Boolean(actionsAnchorEl);

  const handleActionsMenuOpen = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setActionsAnchorEl(e.currentTarget);
  };

  const handleActionSelect = (e: React.MouseEvent, action: () => void): void => {
    e.stopPropagation();
    setActionsAnchorEl(null);
    action();
  };

  return (
    <div
      ref={setNodeRef}
      data-block-index={blockIndex}
      data-sbs-pair={sbsMeta?.pair.pairNumber}
      className={[
        'block-tab',
        !readOnly && !isEditing ? 'has-block-actions' : '',
        isActionsMenuOpen ? 'actions-menu-open' : '',
        isSelected ? 'active' : '',
        isCardsMode ? 'cards-mode' : '',
        isDragging ? 'dragging' : '',
        sbsMeta ? 'sbs-paired' : '',
        sbsMeta?.side === 'left' ? 'sbs-left' : '',
        sbsMeta?.side === 'right' ? 'sbs-right' : '',
        isInvalidDropTarget ? 'invalid-drop-target' : '',
        block.isHidden ? 'hidden' : '',
      ].filter(Boolean).join(' ')}
      style={style}
      onClick={() => onSelect(blockIndex)}
      onMouseEnter={() => onMouseEnter(blockIndex)}
      onMouseLeave={onMouseLeave}
    >
      {isCardsMode && cardGroup && (
        <span className="card-accent-strip" style={{ background: cardGroup.color }} />
      )}
      {sbsMeta && <span className="sbs-accent-strip" />}
      {!readOnly && <button
        type="button"
        className="block-drag-handle"
        disabled={disableReorder}
        ref={setActivatorNodeRef}
        title={t`Reorder block`}
        aria-label={t`Reorder block`}
        onClick={(e) => e.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </button>}
      <span className="block-number">{visibleBlock.blockNumber}</span>
      {isEditing && !readOnly ? (
        <form onSubmit={onRenameSubmit} onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={editingName}
            onChange={(e) => onEditNameChange(e.target.value)}
            onBlur={onRenameCancel}
            onKeyDown={onRenameKeyDown}
            autoFocus
            className="block-rename-input"
            dir="auto"
          />
        </form>
      ) : (
        <div className="block-tab-content">
          <div className="block-name-row">
            {(isFirstInCard || sbsMeta) && (
              <div className="block-tab-badges">
                {isFirstInCard && (
                  <span className="card-badge" style={{ background: cardGroup?.color }}>
                    {cardGroup?.name}
                  </span>
                )}
                {sbsMeta && (
                  <>
                    <span className="sbs-pair-badge">SBS</span>
                    <span className="sbs-side-badge">{sbsMeta.side === 'left' ? 'L' : 'R'}</span>
                  </>
                )}
              </div>
            )}
            <Tooltip title={block.name} enterDelay={300} enterNextDelay={300}>
              <span className="block-name" dir="auto">{block.name}</span>
            </Tooltip>
          </div>
          {!readOnly && (
            <div className="block-actions" aria-hidden={!isHovered && !isActionsMenuOpen}>
              <button
                type="button"
                className="block-action-btn more-actions-btn"
                onClick={handleActionsMenuOpen}
                title={t`Block actions`}
                aria-label={t`Block actions`}
                aria-haspopup="menu"
                aria-expanded={isActionsMenuOpen}
                tabIndex={isHovered || isActionsMenuOpen ? 0 : -1}
              >
                <MoreHorizIcon fontSize="small" />
              </button>
              <Menu
                className="block-actions-menu"
                anchorEl={actionsAnchorEl}
                open={isActionsMenuOpen}
                onClose={() => setActionsAnchorEl(null)}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
              >
                <MenuItem
                  className="block-action-menu-item visibility-action"
                  onClick={(e) => handleActionSelect(e, () => onVisibilityToggle(e, blockIndex))}
                >
                  <ListItemIcon>
                    {block.isHidden ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText>{visibilityLabel}</ListItemText>
                </MenuItem>
                <MenuItem
                  className="block-action-menu-item lock-action"
                  onClick={(e) => handleActionSelect(e, () => onLockToggle(e, blockIndex))}
                >
                  <ListItemIcon>
                    {block.isLocked ? <VpnKeyIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText>{lockLabel}</ListItemText>
                </MenuItem>
                <MenuItem
                  className="block-action-menu-item edit-action"
                  onClick={(e) => handleActionSelect(e, () => onEditClick(e, blockIndex))}
                >
                  <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t`Rename block`}</ListItemText>
                </MenuItem>
                <MenuItem
                  className="block-action-menu-item delete-action"
                  onClick={(e) => handleActionSelect(e, () => onDeleteClick(e, blockIndex))}
                >
                  <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t`Delete block`}</ListItemText>
                </MenuItem>
                {canSplit && onSplitCardAt && (
                  <MenuItem
                    className="block-action-menu-item card-action"
                    onClick={(e) => handleActionSelect(e, () => onSplitCardAt(block.id))}
                  >
                    <ListItemIcon><CallSplitIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t`Start new card here`}</ListItemText>
                  </MenuItem>
                )}
                {canMerge && onMergeCardWithPrevious && cardGroup && (
                  <MenuItem
                    className="block-action-menu-item card-action"
                    onClick={(e) => handleActionSelect(e, () => onMergeCardWithPrevious(cardGroup.id))}
                  >
                    <ListItemIcon><MergeIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t`Merge with previous card`}</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BlockTabs: React.FC<BlockTabsProps> = ({
  blocks,
  selectedBlockIndex,
  onBlockSelect,
  onBlocksChange,
  maxTabsHeight,
  tabsTopOffset,
  isCardsMode = false,
  cardGroups = [],
  onSplitCardAt,
  onMergeCardWithPrevious,
  readOnly = false,
}) => {
  const { selectedProject } = useProject();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const closeSearch = (): void => {
    setSearchQuery('');
    setSearchOpen(false);
    searchButtonRef.current?.focus();
  };
  useEffect(() => { setSearchQuery(''); setSearchOpen(false); }, [selectedProject?.id]);
  const [editingBlockIndex, setEditingBlockIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [hoveredBlockIndex, setHoveredBlockIndex] = useState<number | null>(null);
  const [showCreationPopup, setShowCreationPopup] = useState<boolean>(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteState | null>(null);
  const [invalidDropTargetId, setInvalidDropTargetId] = useState<UniqueIdentifier | null>(null);
  const tabsListRef = useRef<HTMLDivElement>(null);

  const commitBlocksChange = useCallback((newBlocks: Block[]): void => {
    const result = onBlocksChange(newBlocks);
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        console.error('Error in onBlocksChange:', error);
      });
    }
  }, [onBlocksChange]);

  useEffect(() => {
    if (selectedBlockIndex === null || !tabsListRef.current) return;
    const selectedTab: HTMLElement | null = tabsListRef.current.querySelector(
      `.block-tab[data-block-index="${selectedBlockIndex}"]`
    );
    selectedTab?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedBlockIndex, searchOpen]);

  useEffect(() => {
    const nextCursor: string = invalidDropTargetId ? 'not-allowed' : '';
    document.body.style.cursor = nextCursor;
    document.documentElement.style.cursor = nextCursor;

    return () => {
      document.body.style.cursor = '';
      document.documentElement.style.cursor = '';
    };
  }, [invalidDropTargetId]);

  const visibleBlocks = useMemo<VisibleBlock[]>(
    () => blocks
      .map((block: Block, blockIndex: number) => ({ block, blockIndex }))
      .filter(({ block }) => block?.name?.trim() && block.name !== 'undefined')
      .map(({ block, blockIndex }, index: number) => ({ block, blockIndex, blockNumber: index + 1 })),
    [blocks]
  );

  const visibleBlockIds = useMemo<UniqueIdentifier[]>(
    () => visibleBlocks.map(({ block }: VisibleBlock) => block.id),
    [visibleBlocks]
  );
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
  const filteredBlocks = visibleBlocks.filter(({ block }) => block.name.toLocaleLowerCase().includes(normalizedQuery));

  const blockCardMap = useMemo(
    () => (isCardsMode ? buildBlockCardMap(cardGroups) : new Map()),
    [isCardsMode, cardGroups]
  );

  const sbsLookup = useMemo(() => buildSbsPairLookup(blocks), [blocks]);
  const hasSbsBlocks = useMemo(() => hasAnySbsBlocks(blocks), [blocks]);
  const sbsCloneSourceBlockIds = useMemo(
    () => visibleBlocks
      .filter(({ blockIndex }: VisibleBlock) => sbsLookup.byIndex.has(blockIndex))
      .map(({ block }: VisibleBlock) => block.id),
    [sbsLookup, visibleBlocks]
  );

  const deleteBlockIndices = useCallback((indicesToDelete: number[]): void => {
    const uniqueSortedIndices: number[] = Array.from(new Set(indicesToDelete))
      .filter((index: number) => index >= 0 && index < blocks.length)
      .sort((left: number, right: number) => left - right);

    if (uniqueSortedIndices.length === 0) {
      return;
    }

    const indexSet: Set<number> = new Set<number>(uniqueSortedIndices);
    const nextBlocks: Block[] = blocks.filter((_, index: number) => !indexSet.has(index));
    commitBlocksChange(nextBlocks);

    if (selectedBlockIndex === null) {
      return;
    }

    const removedBeforeSelection: number = uniqueSortedIndices.filter(
      (index: number) => index < selectedBlockIndex
    ).length;

    if (indexSet.has(selectedBlockIndex)) {
      onBlockSelect(nextBlocks.length > 0 ? Math.min(uniqueSortedIndices[0], nextBlocks.length - 1) : null);
      return;
    }

    onBlockSelect(selectedBlockIndex - removedBeforeSelection);
  }, [blocks, commitBlocksChange, onBlockSelect, selectedBlockIndex]);

  const handleBlockClick = useCallback((blockIndex: number | null) => {
    onBlockSelect(blockIndex);
    if (blockIndex === null) {
      return;
    }
    setSearchQuery('');
    setSearchOpen(false);
    const block: Block | undefined = blocks[blockIndex];
    trackEvent('block_tab_selected', {
      project_id: selectedProject?.id,
      block_id: block?.id,
      block_name: block?.name,
      block_index: blockIndex,
      is_cards_mode: isCardsMode,
    }, {
      dedupeKey: `${selectedProject?.id ?? 'no_project'}:${block?.id ?? blockIndex}`,
      dedupeMs: 1000,
    });
  }, [blocks, isCardsMode, onBlockSelect, selectedProject?.id]);

  const handleEditClick = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const block = blocks[blockIndex];
    if (block) {
      setEditingBlockIndex(blockIndex);
      setEditingName(block.name);
    }
  }, [blocks]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const sbsMeta: SbsBlockMeta | undefined = sbsLookup.byIndex.get(blockIndex);
    if (sbsMeta) {
      setPendingDelete({
        blockIndex,
        sbsMeta,
      });
      return;
    }

    trackEvent('block_deleted', {
      project_id: selectedProject?.id,
      block_id: blocks[blockIndex]?.id,
      block_name: blocks[blockIndex]?.name,
      delete_scope: 'single',
      has_sbs_pair: false,
    });
    deleteBlockIndices([blockIndex]);
  }, [blocks, deleteBlockIndices, sbsLookup, selectedProject?.id]);

  const handleVisibilityToggle = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const block: Block | undefined = blocks[blockIndex];
    if (!block) {
      return;
    }

    const nextIsHidden: boolean = !block.isHidden;
    const newBlocks: Block[] = blocks.map((candidate: Block, index: number) => (
      index === blockIndex
        ? Block.create({ ...candidate, isHidden: nextIsHidden })
        : candidate
    ));
    commitBlocksChange(newBlocks);
    trackEvent('block_visibility_toggled', {
      project_id: selectedProject?.id,
      block_id: block.id,
      block_name: block.name,
      block_index: blockIndex,
      is_hidden: nextIsHidden,
    });
  }, [blocks, commitBlocksChange, selectedProject?.id]);

  const handleLockToggle = useCallback((e: React.MouseEvent, blockIndex: number) => {
    e.stopPropagation();
    const block: Block | undefined = blocks[blockIndex];
    if (!block) {
      return;
    }

    const nextIsLocked: boolean = !block.isLocked;
    const newBlocks: Block[] = blocks.map((candidate: Block, index: number) => (
      index === blockIndex
        ? Block.create({ ...candidate, isLocked: nextIsLocked, dataRows: candidate.dataRows })
        : candidate
    ));
    commitBlocksChange(newBlocks);
    if (nextIsLocked) {
      trackEvent('block_locked', {
        block_name: block.name,
        project_name: selectedProject?.name,
      });
    }
  }, [blocks, commitBlocksChange, selectedProject?.name]);

  const handleRenameSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (editingBlockIndex !== null && editingName.trim()) {
      const nextName: string = editingName.trim();
      const isDuplicateName: boolean = blocks.some(
        (block: Block, blockIndex: number) => blockIndex !== editingBlockIndex && block.name.trim() === nextName
      );
      if (isDuplicateName) {
        alert('Block names must be unique. Choose a different name.');
        return;
      }
      const newBlocks: Block[] = [...blocks];
      const previousName: string | undefined = newBlocks[editingBlockIndex]?.name;
      newBlocks[editingBlockIndex] = Block.create({
        ...newBlocks[editingBlockIndex],
        name: nextName,
      });
      commitBlocksChange(newBlocks);
      trackEvent('block_tab_renamed', {
        project_id: selectedProject?.id,
        block_id: newBlocks[editingBlockIndex]?.id,
        previous_name: previousName,
        next_name: nextName,
      });
      setEditingBlockIndex(null);
      setEditingName('');
    }
  }, [blocks, commitBlocksChange, editingBlockIndex, editingName, selectedProject?.id]);

  const handleRenameCancel = useCallback(() => {
    setEditingBlockIndex(null);
    setEditingName('');
  }, []);

  const handleAddBlock = useCallback(() => {
    setShowCreationPopup(true);
    trackEvent('block_creation_popup_opened', {
      project_id: selectedProject?.id,
      existing_block_count: blocks.length,
      is_cards_mode: isCardsMode,
    });
  }, [blocks.length, isCardsMode, selectedProject?.id]);

  const handleCreate = useCallback((request: BlockCreationRequest) => {
    setSearchQuery('');
    setSearchOpen(false);
    if (request.type === 'empty') {
      const existingNames: Set<string> = new Set<string>(blocks.map((block: Block) => block.name));
      const createUniqueEmptyBlock = (baseName: string, sbsBlocks: Block['sbsBlocks'] = 'normal'): Block => {
        const name: string = getUniqueBlockName(baseName, existingNames);
        existingNames.add(name);
        return createEmptyBlock(name, sbsBlocks);
      };
      const createdBlocks: Block[] = request.createSbsPair
        ? [
            createUniqueEmptyBlock(`Block ${blocks.length + 1}`, 'left'),
            createUniqueEmptyBlock(`Block ${blocks.length + 2}`, 'right'),
          ]
        : [createUniqueEmptyBlock(`Block ${blocks.length + 1}`)];
      const nextBlocks: Block[] = [...blocks, ...createdBlocks];
      const nextSelectedIndex: number = blocks.length;

      commitBlocksChange(nextBlocks);
      trackEvent('block_created', {
        project_id: selectedProject?.id,
        block_id: createdBlocks[0]?.id,
        block_name: createdBlocks[0]?.name,
        source: 'empty',
        create_sbs_pair: request.createSbsPair,
        created_count: createdBlocks.length,
      });
      onBlockSelect(nextSelectedIndex);
      setEditingBlockIndex(nextSelectedIndex);
      setEditingName(createdBlocks[0].name);
      return;
    }

    const sourceIndex: number = blocks.findIndex((block: Block) => block.id === request.sourceBlockId);
    const cloneResult = cloneBlocksForSource(
      blocks,
      sourceIndex,
      String(t`(Copy)`),
      request.createSbsPair
    );
    if (!cloneResult) {
      return;
    }

    commitBlocksChange(cloneResult.blocks);
    trackEvent('block_created', {
      project_id: selectedProject?.id,
      block_id: cloneResult.blocks[cloneResult.selectedIndex]?.id,
      block_name: cloneResult.blocks[cloneResult.selectedIndex]?.name,
      source: 'clone',
      create_sbs_pair: request.createSbsPair,
      source_block_id: request.sourceBlockId,
      created_count: request.createSbsPair ? 2 : 1,
    });
    onBlockSelect(cloneResult.selectedIndex);
    setEditingBlockIndex(cloneResult.selectedIndex);
    setEditingName(cloneResult.blocks[cloneResult.selectedIndex].name);
  }, [blocks, commitBlocksChange, onBlockSelect, selectedProject?.id]);

  const getBlockIndexById = useCallback((id: UniqueIdentifier): number => {
    return blocks.findIndex((block: Block) => block.id === id);
  }, [blocks]);

  const getDragMoveState = useCallback((activeId: UniqueIdentifier, overId: UniqueIdentifier | null) => {
    if (!overId) {
      return null;
    }

    const sourceIndex: number = getBlockIndexById(activeId);
    const targetIndex: number = getBlockIndexById(overId);

    if (sourceIndex < 0 || targetIndex < 0) {
      return null;
    }

    const sourceCardGroup: CardGroup | undefined = isCardsMode
      ? blockCardMap.get(blocks[sourceIndex]?.id)
      : undefined;
    const moveRange = getBlockTabsMoveRangeForBlockIndex(
      blocks,
      sourceIndex,
      isCardsMode,
      sourceCardGroup
    );
    const dropValidation = getSbsDropValidation(
      blocks,
      moveRange.startIndex,
      moveRange.endIndex,
      targetIndex
    );

    return {
      sourceIndex,
      targetIndex,
      moveRange,
      dropValidation,
    };
  }, [blockCardMap, blocks, getBlockIndexById, isCardsMode]);

  const handleDragEnd = useCallback((event: DragEndEvent): void => {
    if (normalizedQuery) return;
    const { active, over } = event;
    setInvalidDropTargetId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const moveState = getDragMoveState(active.id, over.id);
    if (!moveState || moveState.sourceIndex === moveState.targetIndex) {
      return;
    }

    const { sourceIndex, targetIndex, moveRange, dropValidation } = moveState;
    if (dropValidation.isBlocked) {
      return;
    }

    const moveResult = moveBlockRange(
      blocks,
      moveRange.startIndex,
      moveRange.endIndex,
      targetIndex
    );

    if (!moveResult) {
      return;
    }

    commitBlocksChange(moveResult.blocks);
    trackEvent('block_tabs_reordered', {
      project_id: selectedProject?.id,
      moved_block_id: blocks[sourceIndex]?.id,
      moved_block_name: blocks[sourceIndex]?.name,
      target_block_id: blocks[targetIndex]?.id,
      source_index: sourceIndex,
      target_index: targetIndex,
      is_cards_mode: isCardsMode,
    });

    if (selectedBlockIndex === null) {
      return;
    }

    const draggedBlockNextIndex: number = remapIndexAfterRangeMove(
      sourceIndex,
      moveRange.startIndex,
      moveRange.endIndex,
      targetIndex
    );
    const selectedBlockNextIndex: number = remapIndexAfterRangeMove(
      selectedBlockIndex,
      moveRange.startIndex,
      moveRange.endIndex,
      targetIndex
    );
    const isSelectionInsideMovedRange: boolean =
      selectedBlockIndex >= moveRange.startIndex &&
      selectedBlockIndex <= moveRange.endIndex;

    onBlockSelect(isSelectionInsideMovedRange ? draggedBlockNextIndex : selectedBlockNextIndex);
  }, [blocks, commitBlocksChange, getDragMoveState, isCardsMode, onBlockSelect, selectedBlockIndex, selectedProject?.id, normalizedQuery]);

  const handleDragStart = useCallback((_event: DragStartEvent): void => {
    setInvalidDropTargetId(null);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent): void => {
    const { active, over } = event;
    const moveState = getDragMoveState(active.id, over?.id ?? null);
    setInvalidDropTargetId(moveState?.dropValidation.isBlocked ? over?.id ?? null : null);
  }, [getDragMoveState]);

  const handleDragCancel = useCallback((_event: DragCancelEvent): void => {
    setInvalidDropTargetId(null);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleRenameCancel();
    }
  }, [handleRenameCancel]);

  const tabsStyle: React.CSSProperties = {
    ...(maxTabsHeight ? { maxHeight: `${maxTabsHeight}px` } : {}),
    ...(tabsTopOffset ? { marginTop: `${tabsTopOffset}px` } : {}),
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const pendingDeleteBlockName: string = pendingDelete
    ? blocks[pendingDelete.blockIndex]?.name ?? ''
    : '';
  const pendingDeletePartnerName: string = pendingDelete
    ? blocks[pendingDelete.sbsMeta.partnerIndex]?.name ?? ''
    : '';

  return (
    <div
      className={[
        'block-tabs-container',
        invalidDropTargetId ? 'invalid-drop' : '',
      ].filter(Boolean).join(' ')}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={visibleBlockIds} strategy={verticalListSortingStrategy}>
          <div className="block-tabs" style={tabsStyle} ref={tabsListRef}>
            {filteredBlocks.length === 0 && normalizedQuery && <p className="block-search-empty" role="status">{t`No matching blocks`}</p>}
            {filteredBlocks.map((visibleBlock: VisibleBlock) => {
              const { block, blockIndex } = visibleBlock;
              const isSelected: boolean = selectedBlockIndex === blockIndex;
              const isEditing: boolean = editingBlockIndex === blockIndex;
              const isHovered: boolean = hoveredBlockIndex === blockIndex;
              const cardGroup: CardGroup | undefined = isCardsMode ? blockCardMap.get(block.id) : undefined;
              const isFirstInCard: boolean = isCardsMode && cardGroup !== undefined && cardGroup.blockIds[0] === block.id;
              const isFirstCard: boolean = isFirstInCard && cardGroups[0]?.id === cardGroup?.id;
              const canSplit: boolean = isCardsMode && cardGroup !== undefined && !isFirstInCard;
              const canMerge: boolean = isCardsMode && isFirstInCard && !isFirstCard;
              const sbsMeta: SbsBlockMeta | undefined = sbsLookup.byIndex.get(blockIndex);
              const isInvalidDropTarget: boolean = invalidDropTargetId === block.id;

              return (
                <SortableBlockTab
                  key={block.id}
                  visibleBlock={visibleBlock}
                  isSelected={isSelected}
                  isEditing={isEditing}
                  isHovered={isHovered}
                  isCardsMode={isCardsMode}
                  cardGroup={cardGroup}
                  sbsMeta={sbsMeta}
                  isInvalidDropTarget={isInvalidDropTarget}
                  editingName={editingName}
                  canSplit={canSplit}
                  canMerge={canMerge}
                  readOnly={readOnly}
                  disableReorder={Boolean(normalizedQuery)}
                  onSelect={handleBlockClick}
                  onMouseEnter={setHoveredBlockIndex}
                  onMouseLeave={() => setHoveredBlockIndex(null)}
                  onRenameSubmit={handleRenameSubmit}
                  onRenameCancel={handleRenameCancel}
                  onRenameKeyDown={handleKeyDown}
                  onEditNameChange={setEditingName}
                  onEditClick={handleEditClick}
                  onDeleteClick={handleDeleteClick}
                  onVisibilityToggle={handleVisibilityToggle}
                  onLockToggle={handleLockToggle}
                  onSplitCardAt={onSplitCardAt ? (blockId: string) => {
                    const block = blocks.find((candidate: Block) => candidate.id === blockId);
                    trackEvent('card_group_split_requested', {
                      project_id: selectedProject?.id,
                      block_id: blockId,
                      block_name: block?.name,
                    });
                    onSplitCardAt(blockId);
                  } : undefined}
                  onMergeCardWithPrevious={onMergeCardWithPrevious ? (cardId: CardId) => {
                    trackEvent('card_group_merge_requested', {
                      project_id: selectedProject?.id,
                      card_id: cardId,
                    });
                    onMergeCardWithPrevious(cardId);
                  } : undefined}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
      <div className="block-list-tools">
      <button ref={searchButtonRef} type="button" className="block-search-button" aria-label={t`Search blocks`} aria-expanded={searchOpen} onClick={() => searchOpen ? closeSearch() : setSearchOpen(true)}><SearchIcon fontSize="small" /></button>
      {searchOpen && <div className="block-search-field"><input autoFocus aria-label={t`Search blocks by name`} placeholder={t`Search blocks`} dir="auto" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); closeSearch(); } }} /><button type="button" className="block-search-button" aria-label={t`Close search`} onClick={closeSearch}><CloseIcon fontSize="small" /></button></div>}
      {!readOnly && <button
        className="add-block-btn"
        onClick={handleAddBlock}
        title={t`Add new block`}
      >
        <AddIcon fontSize="small" />
      </button>}
      </div>

      {!readOnly && <BlockCreationPopup
        isOpen={showCreationPopup}
        onClose={() => setShowCreationPopup(false)}
        onCreate={handleCreate}
        existingBlocks={visibleBlocks.map(({ block }: VisibleBlock) => block)}
        hasSbsBlocks={hasSbsBlocks}
        sbsCloneSourceBlockIds={sbsCloneSourceBlockIds}
      />}

      {!readOnly && <SbsDeleteDialog
        open={pendingDelete !== null}
        blockName={pendingDeleteBlockName}
        partnerName={pendingDeletePartnerName}
        onCancel={() => setPendingDelete(null)}
        onDeleteOnly={() => {
          if (!pendingDelete) return;
          trackEvent('block_deleted', {
            project_id: selectedProject?.id,
            block_id: blocks[pendingDelete.blockIndex]?.id,
            block_name: blocks[pendingDelete.blockIndex]?.name,
            delete_scope: 'single',
            has_sbs_pair: true,
          });
          deleteBlockIndices([pendingDelete.blockIndex]);
          setPendingDelete(null);
        }}
        onDeleteBoth={() => {
          if (!pendingDelete) return;
          trackEvent('block_deleted', {
            project_id: selectedProject?.id,
            block_id: blocks[pendingDelete.blockIndex]?.id,
            block_name: blocks[pendingDelete.blockIndex]?.name,
            partner_block_id: blocks[pendingDelete.sbsMeta.partnerIndex]?.id,
            delete_scope: 'pair',
            has_sbs_pair: true,
          });
          deleteBlockIndices([
            pendingDelete.sbsMeta.pair.startIndex,
            pendingDelete.sbsMeta.pair.endIndex,
          ]);
          setPendingDelete(null);
        }}
      />}
    </div>
  );
};

export default BlockTabs;
